import React, { useState, useRef, useEffect } from 'react';
import { FormInputModes } from './FormInputModes';
const STICKY_TOP_OFFSET = 10;
const TOOLTIP_STORAGE_KEY = 'fluidform_input_modes_tooltip_seen';
const TOOLTIP_AUTO_DISMISS_MS = 8000;
// Build dynamic tooltip message based on available input modes
const buildTooltipMessage = (modes) => {
    const features = [];
    if (modes.includes('speech') || modes.includes('streamSpeech')) {
        features.push(React.createElement("strong", { key: "voice" }, "voice"));
    }
    if (modes.includes('fileUpload')) {
        features.push(React.createElement("strong", { key: "files" }, "files"));
    }
    if (modes.includes('camera')) {
        features.push(React.createElement("strong", { key: "camera" }, "camera"));
    }
    if (features.length === 0)
        return null;
    // Join features with commas and "or" for the last one
    const joinedFeatures = features.reduce((acc, feature, i) => {
        if (i === 0)
            return [feature];
        if (i === features.length - 1)
            return [...acc, ' or ', feature];
        return [...acc, ', ', feature];
    }, []);
    return React.createElement(React.Fragment, null,
        "Use AI to fill this form with your ",
        joinedFeatures,
        ".");
};
const DEFAULT_INPUT_MODES = ['speech', 'streamSpeech', 'fileUpload', 'camera', 'urlCapture'];
export const FloatingInputModes = ({ initialOffset = { top: 8, right: 8 }, formRef, ...inputModesProps }) => {
    const nodeRef = useRef(null);
    const inputModes = inputModesProps.inputModes || DEFAULT_INPUT_MODES;
    const tooltipMessage = buildTooltipMessage(inputModes);
    const [isSticky, setIsSticky] = useState(false);
    const [fixedLeft, setFixedLeft] = useState(null);
    const [showTooltip, setShowTooltip] = useState(false);
    const [isDismissing, setIsDismissing] = useState(false);
    // First-time tooltip logic
    useEffect(() => {
        const showTimer = setTimeout(() => setShowTooltip(true), 500);
        return () => clearTimeout(showTimer);
    }, []);
    // Auto-dismiss tooltip after delay
    useEffect(() => {
        if (showTooltip && !isDismissing) {
            const dismissTimer = setTimeout(() => {
                dismissTooltip();
            }, TOOLTIP_AUTO_DISMISS_MS);
            return () => clearTimeout(dismissTimer);
        }
    }, [showTooltip, isDismissing]);
    const dismissTooltip = () => {
        if (isDismissing)
            return;
        setIsDismissing(true);
    };
    const handleAnimationEnd = () => {
        if (isDismissing) {
            setShowTooltip(false);
            setIsDismissing(false);
            localStorage.setItem(TOOLTIP_STORAGE_KEY, 'true');
        }
    };
    // Handle sticky scroll behavior
    // Switches between absolute (natural) and fixed (sticky) positioning
    useEffect(() => {
        const updateStickyState = () => {
            if (!formRef?.current) {
                setIsSticky(false);
                return;
            }
            const formRect = formRef.current.getBoundingClientRect();
            // If form top + offset is visible, use absolute positioning (natural position)
            // Otherwise switch to fixed positioning (sticky)
            const shouldBeSticky = formRect.top + initialOffset.top < STICKY_TOP_OFFSET;
            setIsSticky(shouldBeSticky);
            // Calculate the left position for fixed mode based on form position
            if (nodeRef.current) {
                const elementWidth = nodeRef.current.offsetWidth;
                const calculatedLeft = formRect.right - initialOffset.right - elementWidth;
                setFixedLeft(calculatedLeft);
            }
        };
        updateStickyState();
        window.addEventListener('scroll', updateStickyState, { passive: true });
        window.addEventListener('resize', updateStickyState, { passive: true });
        return () => {
            window.removeEventListener('scroll', updateStickyState);
            window.removeEventListener('resize', updateStickyState);
        };
    }, [formRef, initialOffset]);
    const containerClasses = [
        'flex items-center px-1 py-1',
        'bg-white/20 rounded-3xl',
        'shadow-[0_4px_16px_rgba(0,0,0,0.12),0_0_0_1px_rgba(255,255,255,0.5)_inset]',
        'border border-white/30 select-none',
    ].join(' ');
    // Wrapper styles: fixed (sticky) or absolute (natural)
    const wrapperStyles = isSticky && fixedLeft !== null
        ? {
            position: 'fixed',
            top: STICKY_TOP_OFFSET,
            left: fixedLeft,
            zIndex: 1000,
        }
        : {
            position: 'absolute',
            top: initialOffset.top,
            right: initialOffset.right,
            zIndex: 1000,
        };
    return (React.createElement("div", { style: wrapperStyles },
        React.createElement("div", { ref: nodeRef, className: containerClasses, style: {
                backdropFilter: 'blur(2px)',
                WebkitBackdropFilter: 'blur(2px)',
            } },
            React.createElement(FormInputModes, { ...inputModesProps })),
        showTooltip && tooltipMessage && (React.createElement(React.Fragment, null,
            React.createElement("style", null, `
            @keyframes tooltipFadeIn {
              from { opacity: 0; transform: translateY(-4px); }
              to { opacity: 1; transform: translateY(0); }
            }
            @keyframes tooltipFadeOut {
              from { opacity: 1; transform: translateY(0); }
              to { opacity: 0; transform: translateY(-4px); }
            }
          `),
            React.createElement("div", { className: "absolute top-full right-0 mt-2", style: {
                    animation: isDismissing
                        ? 'tooltipFadeOut 0.2s ease-in forwards'
                        : 'tooltipFadeIn 0.3s ease-out',
                }, onAnimationEnd: handleAnimationEnd },
                React.createElement("div", { className: "relative bg-white/20 text-gray-900 text-sm px-4 py-3 rounded-lg w-[210px] cursor-pointer border border-white/30 shadow-[0_4px_16px_rgba(0,0,0,0.12),0_0_0_1px_rgba(255,255,255,0.5)_inset]", style: {
                        backdropFilter: 'blur(7px)',
                        WebkitBackdropFilter: 'blur(7px)',
                    }, onClick: dismissTooltip, role: "tooltip" },
                    React.createElement("div", { className: "absolute -top-2 right-4 w-0 h-0", style: {
                            borderLeft: '8px solid transparent',
                            borderRight: '8px solid transparent',
                            borderBottom: '8px solid rgba(255, 255, 255, 0.8)',
                        } }),
                    React.createElement("p", { className: "leading-relaxed" }, tooltipMessage),
                    React.createElement("button", { className: "mt-2 text-xs text-gray-500 hover:text-gray-900 transition-colors", onClick: (e) => {
                            e.stopPropagation();
                            dismissTooltip();
                        } }, "Got it")))))));
};
export default FloatingInputModes;
